import { useCallback, useEffect, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { FormField, inputClass } from "../components/FormField";
import { getAuthToken } from "../auth";
import {
  createHospitalPharmacyProduct,
  deleteHospitalPharmacyProduct,
  fetchHospitalPharmacyProducts,
  type HospitalPharmacyProduct,
} from "../api/hospitalApi";

export function HospitalPharmacyCatalogPage() {
  const [products, setProducts] = useState<HospitalPharmacyProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [code, setCode] = useState("");
  const [label, setLabel] = useState("");
  const [price, setPrice] = useState("");
  const [saving, setSaving] = useState(false);

  const reload = useCallback(async () => {
    if (!getAuthToken()) return;
    setLoading(true);
    try {
      const response = await fetchHospitalPharmacyProducts();
      setProducts(response.products ?? []);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Catalogue indisponible pour ce compte.");
      setProducts([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  const handleCreate = async () => {
    setSaving(true);
    try {
      await createHospitalPharmacyProduct({
        code: code.trim(),
        label: label.trim(),
        unit_price_usd: Number(price),
        status: "active",
      });
      setCode("");
      setLabel("");
      setPrice("");
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Création impossible.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (productId: number) => {
    if (!window.confirm("Supprimer ce produit ?")) return;
    try {
      await deleteHospitalPharmacyProduct(productId);
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Suppression impossible.");
    }
  };

  return (
    <>
      <Breadcrumbs homeHref="/dashboard/hospital" items={[{ label: "Catalogue pharmacie" }]} />

      <div className="mb-6">
        <h1 className="text-xl font-bold text-foreground">Mon catalogue produits</h1>
        <p className="mt-1 text-sm text-muted">Réservé aux comptes prestataires de type pharmacie.</p>
      </div>

      <div className="theme-card mb-5 p-5">
        <h2 className="mb-4 text-sm font-semibold text-foreground">Ajouter un produit</h2>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
          <FormField label="Code">
            <input value={code} onChange={(e) => setCode(e.target.value)} className={inputClass} />
          </FormField>
          <FormField label="Libellé">
            <input value={label} onChange={(e) => setLabel(e.target.value)} className={inputClass} />
          </FormField>
          <FormField label="Prix USD">
            <input
              type="number"
              min="0"
              step="0.01"
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              className={inputClass}
            />
          </FormField>
          <div className="flex items-end">
            <button
              type="button"
              disabled={saving || !code.trim() || !label.trim() || !price}
              onClick={() => void handleCreate()}
              className="inline-flex h-10 items-center gap-2 rounded-lg bg-primary px-4 text-sm font-medium text-white disabled:opacity-60"
            >
              <Plus size={15} />
              Ajouter
            </button>
          </div>
        </div>
      </div>

      {error ? <p className="mb-3 text-sm text-red-600">{error}</p> : null}
      {loading ? <p className="text-sm text-muted">Chargement…</p> : null}

      <div className="theme-card overflow-x-auto">
        <table className="w-full min-w-[640px]">
          <thead>
            <tr className="border-b border-border bg-card-muted/40 text-left text-xs font-semibold uppercase text-muted">
              <th className="px-4 py-3">Code</th>
              <th className="px-4 py-3">Libellé</th>
              <th className="px-4 py-3 text-right">Prix USD</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {products.map((product) => (
              <tr key={product.id} className="border-b border-border last:border-0">
                <td className="px-4 py-3 text-sm font-medium">{product.code}</td>
                <td className="px-4 py-3 text-sm">{product.label}</td>
                <td className="px-4 py-3 text-right text-sm">
                  {Number(product.unit_price_usd).toFixed(2)}
                </td>
                <td className="px-4 py-3 text-right">
                  <button
                    type="button"
                    onClick={() => void handleDelete(product.id)}
                    className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-border text-danger hover:bg-danger-soft"
                  >
                    <Trash2 size={14} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}
